import PropTypes from 'prop-types';
import { useMemo, useContext } from 'react';

// material-ui
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

// project imports
import useConfig from 'hooks/useConfig';
import { AuthContext } from 'contexts/AuthContext';
import CustomShadows from './custom-shadows';
import componentsOverride from './overrides';
import { buildPalette } from './palette';
import Typography from './typography';

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
  const { state } = useConfig();
  const { user } = useContext(AuthContext);

  const themeTypography = useMemo(() => Typography(state.fontFamily), [state.fontFamily]);

  const primaryColor = user?.clinic?.primary_color || user?.clinic?.branding?.primary_color;
  const secondaryColor = user?.clinic?.secondary_color || user?.clinic?.branding?.secondary_color;

  const palette = useMemo(
    () => buildPalette(state.presetColor, primaryColor, secondaryColor, state.darkModeStyle),
    [state.presetColor, primaryColor, secondaryColor, state.darkModeStyle]
  );

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: 'ltr',
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      typography: themeTypography,
      colorSchemes: {
        light: {
          palette: palette.light,
          customShadows: CustomShadows(palette.light, 'light')
        },
        dark: {
          palette: palette.dark,
          customShadows: CustomShadows(palette.dark, 'dark')
        }
      },
      cssVariables: {
        cssVarPrefix: '',
        colorSchemeSelector: 'data-color-scheme'
      }
    }),
    [themeTypography, palette]
  );

  const themes = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider disableTransitionOnChange theme={themes} modeStorageKey="theme-mode" defaultMode="dark">
        <CssBaseline enableColorScheme />
        <GlobalStyles
          styles={(theme) => ({
            ':root': {
              '--glow-color': theme.palette.primary.main,
              '--glow-shadow': `0 0 10px ${theme.palette.primary.main}50`,
              '--glow-shadow-hover': `0 0 18px ${theme.palette.primary.main}a0`
            },
            '::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '::-webkit-scrollbar-track': {
              background: theme.palette.background.default
            },
            '::-webkit-scrollbar-thumb': {
              background: theme.palette.primary.light + '40',
              borderRadius: '4px',
              border: `2px solid ${theme.palette.background.default}`
            },
            '::-webkit-scrollbar-thumb:hover': {
              background: theme.palette.primary.main
            },
            body: {
              transition: 'background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), color 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
            },
            '.MuiPaper-root, .MuiCard-root, .MuiButton-root, .MuiListItemButton-root, .MuiDrawer-paper, .MuiAppBar-root': {
              transition: 'background-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), backdrop-filter 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important'
            },
            '.glass-panel': {
              backgroundColor: 'rgba(20, 20, 20, 0.45) !important',
              backdropFilter: 'blur(20px) saturate(130%) !important',
              backgroundImage: 'none !important',
              borderRight: '1px solid rgba(255, 255, 255, 0.08) !important',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08) !important'
            },
            '.glass-card': {
              backgroundColor: 'rgba(30, 30, 30, 0.45) !important',
              backdropFilter: 'blur(20px) saturate(130%) !important',
              backgroundImage: 'none !important',
              border: '1px solid rgba(255, 255, 255, 0.08) !important',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.4) !important'
            }
          })}
        />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
